import { useCallback, useMemo, useState } from "react";

import { cx } from "@emotion/css";
import { type CellProps, Tooltip } from "@snapchat/snap-design-system";

import { LensIcon, Visible } from "../../../common/components";
import { Flexbox } from "../../../core/components";
import { type GroupsGridLensModel } from "../../grid/model/GroupsGridLensModel";

import {
    TooltipId,
    TooltipName,
    hoveredImgCss,
    imgContainerCss,
    imgCss,
    notHoveredIconCss,
    viewMoreTextCss,
    visibleImgCss,
    whiteImgCss,
    wrapperCss,
} from "./LensesCell.styles";

interface LensesCellProps extends Omit<CellProps, "children"> {
    lenses: readonly GroupsGridLensModel[];
    totalNumLenses: number;
    lensesLimit?: number;
}

function LensTooltipContent({ lens }: { lens: GroupsGridLensModel }) {
    return (
        <Flexbox flexDirection="column" alignItems="center">
            <TooltipName>{lens.name}</TooltipName>
            <TooltipId>ID {lens.id}</TooltipId>
        </Flexbox>
    );
}

export function LensesCell({ lenses, totalNumLenses, lensesLimit = 20 }: LensesCellProps) {
    const [hoveredLensId, setHoveredLensId] = useState<string>();

    const onTooltipVisible = useCallback((lensId: string, visible: boolean) => {
        setHoveredLensId(visible ? lensId : undefined);
    }, []);

    const showViewMore = useMemo(() => totalNumLenses > lensesLimit, [lensesLimit, totalNumLenses]);

    return (
        <Flexbox className={wrapperCss}>
            {lenses.slice(0, lensesLimit).map((lens) => (
                <Tooltip
                    key={lens.compId}
                    placement="top"
                    onVisibleChange={(visible) => {
                        onTooltipVisible(lens.compId as string, visible);
                    }}
                    content={<LensTooltipContent lens={lens} />}
                >
                    <div className={imgContainerCss}>
                        {/* background white image to handle fading of transparent image */}
                        <LensIcon
                            src={lens.iconUrl as string}
                            size={40}
                            source={lens.lensSource as string}
                            className={whiteImgCss}
                            badgeClassName={cx({
                                [notHoveredIconCss]: !(hoveredLensId === lens.compId),
                                [visibleImgCss]: !hoveredLensId || hoveredLensId === lens.compId,
                            })}
                        />
                        {/* actual image */}
                        <LensIcon
                            src={lens.iconUrl as string}
                            size={40}
                            className={cx(imgCss, {
                                [visibleImgCss]: !hoveredLensId || hoveredLensId === lens.compId,
                                [hoveredImgCss]: hoveredLensId === lens.compId,
                            })}
                        />
                    </div>
                </Tooltip>
            ))}
            <Visible when={showViewMore}>
                <div className={viewMoreTextCss}>...view all</div>
            </Visible>
        </Flexbox>
    );
}
